// C:\Users\purplehat\Desktop\payment-cookie-system\frontend\src\App.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster, toast } from 'react-hot-toast';
import Dashboard from './components/Dashboard';
import CookieList from './components/CookieList';
import AddCookie from './components/AddCookie';
import Statistics from './components/Statistics';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import Profile from './components/Profile';
import CardManager from './components/CardManager';
import CardBatchManager from './components/CardBatchManager';
import BottomNav from './components/BottomNav';
import ActivityFeed from './components/ActivityFeed';
import LiveStats from './components/LiveStats';
import AmazonCardsPanel from './components/AmazonCardsPanel';
import AmazonAutoPanel from './components/AmazonAutoPanel';
import AmazonAccountCreator from './components/AmazonAccountCreator';
import AdminPanel from './components/AdminPanel';
import socketService from './services/socket';
import api from './services/api';
import './App.css';
import SmsManager from './components/SmsManager';
import WalletManager from './components/WalletManager';
import ChatApp from './components/ChatApp';
import VirtualOperator from './components/VirtualOperator';
import { HomeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

export const ChatNotificationContext = React.createContext();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1, staleTime: 30000 }
  }
});

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem('user');
      return u ? JSON.parse(u) : null;
    } catch (e) { return null; }
  });
  const [loading, setLoading] = useState(false);

  const userRole = user?.role || null;
  const isPending = user?.status === 'pending' || user?.role === 'pending';

  const [totalUnreadMessages, setTotalUnreadMessages] = useState(() => {
    const saved = localStorage.getItem('unreadMessagesCount');
    return saved ? parseInt(saved, 10) : 0;
  });

  const locationRef = useRef(location.pathname);

  useEffect(() => {
    localStorage.setItem('unreadMessagesCount', totalUnreadMessages.toString());
  }, [totalUnreadMessages]);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  const syncAuthFromStorage = useCallback(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
    } else {
      delete api.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
    }

    if (userStr) {
      try { setUser(JSON.parse(userStr)); } catch (e) { setUser(null); }
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    syncAuthFromStorage();
  }, [syncAuthFromStorage]);

  const setAuth = useCallback((auth, authUser) => {
    if (auth) {
      if (authUser) {
        try {
          localStorage.setItem('user', JSON.stringify(authUser));
          setUser(authUser);
        } catch (e) {}
      }
      syncAuthFromStorage();
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [syncAuthFromStorage]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !isAuthenticated) syncAuthFromStorage();
  }, [location.pathname, isAuthenticated, syncAuthFromStorage]);

  // Cargar contador de no leídos
  useEffect(() => {
    if (!isAuthenticated) return;
    const loadUnreadCount = async () => {
      try {
        const response = await api.get('/chat/chats');
        if (response.data?.chats) {
          const unreadCount = response.data.chats.reduce((t, c) => t + (c.unreadCount || 0), 0);
          if (unreadCount > 0) setTotalUnreadMessages(unreadCount);
        }
      } catch (error) {
        console.error('Error cargando mensajes no leídos:', error);
      }
    };
    loadUnreadCount();
  }, [isAuthenticated]);

  // Socket - notificaciones
  useEffect(() => {
    if (!isAuthenticated) return;
    socketService.connect();

    const handleNewMessageNotification = (data) => {
      if (locationRef.current !== '/chat') {
        const msgText = data?.message?.text?.substring(0, 50) || 'Nuevo mensaje';
        toast(`💬 ${msgText}`, {
          icon: '💬',
          duration: 4000,
          style: { cursor: 'pointer', background: '#1a1a1a', color: '#fff', border: '1px solid #333' }
        });
        setTotalUnreadMessages(prev => prev + 1);
      }
    };

    const handleNewMessage = () => {
      if (locationRef.current !== '/chat') setTotalUnreadMessages(prev => prev + 1);
    };

    socketService.on('new_message_notification', handleNewMessageNotification);
    socketService.on('new_message', handleNewMessage);

    return () => {
      socketService.off('new_message_notification', handleNewMessageNotification);
      socketService.off('new_message', handleNewMessage);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (location.pathname === '/chat') setTotalUnreadMessages(0);
  }, [location.pathname]);

  const resetUnreadMessages = useCallback(() => setTotalUnreadMessages(0), []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loader"></div>
      </div>
    );
  }

  if (isAuthenticated && isPending && location.pathname !== '/profile' && location.pathname !== '/login') {
    return <Navigate to="/profile" replace />;
  }

  const isChatRoute = location.pathname === '/chat';
  const showBottomNav = isAuthenticated && location.pathname !== '/login' && !isPending && !isChatRoute;

  const hideLiveStatsRoutes = [
    '/login', '/reset-password', '/add-cookie', '/profile', '/cards', '/cards-batch',
    '/amazon-cards', '/amazon-auto', '/amazon-create', '/admin', '/wallet',
    '/chat', '/sms', '/virtual-operator'
  ];
  const showLiveStats = isAuthenticated && !hideLiveStatsRoutes.includes(location.pathname) && !isPending;
  const showActivityFeed = isAuthenticated && !isPending && !isChatRoute;

  return (
    <ChatNotificationContext.Provider value={{ totalUnreadMessages, setTotalUnreadMessages, resetUnreadMessages }}>
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        ...(isChatRoute ? { height: '100vh', overflow: 'hidden' } : {})
      }}>
        <Toaster position="top-center" toastOptions={{ duration: 3000, style: { background: '#111111', color: '#ffffff', border: '1px solid #222222', borderRadius: '1rem', padding: '1rem', fontSize: '0.9rem' } }} />

        {isChatRoute && (
          <div style={{ background: '#0a0a0a', borderBottom: '1px solid #222', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', zIndex: 30, flexShrink: 0 }}>
            <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.75rem', padding: '0.4rem 0.75rem', color: '#0ff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>
              <ArrowLeftIcon style={{ width: '1rem' }} /> Volver al Inicio
            </button>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af', background: '#1a1a1a', padding: '0.3rem 0.6rem', borderRadius: '0.5rem' }}>💬 Chat</span>
          </div>
        )}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', ...(isChatRoute ? { overflow: 'hidden', minHeight: 0 } : { overflow: 'visible' }) }}>
          <Routes>
            {/* ✅ RUTA PÚBLICA: reset password (sin auth) */}
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/login" element={
              isAuthenticated
                ? <Navigate to={isPending ? "/profile" : "/"} replace />
                : <Login setAuth={setAuth} />
            } />
            <Route path="/" element={isAuthenticated && !isPending ? <Dashboard /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/cookies" element={isAuthenticated && !isPending ? <CookieList /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/add-cookie" element={isAuthenticated && !isPending ? <AddCookie /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/statistics" element={isAuthenticated && !isPending ? <Statistics /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/profile" element={isAuthenticated ? <Profile /> : <Navigate to="/login" replace />} />
            <Route path="/sms" element={isAuthenticated && !isPending ? <SmsManager /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/wallet" element={isAuthenticated && !isPending ? <WalletManager /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/chat" element={isAuthenticated && !isPending ? <ChatApp /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/cards" element={isAuthenticated && !isPending ? <CardManager /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/cards-batch" element={isAuthenticated && !isPending ? <CardBatchManager /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/amazon-cards" element={isAuthenticated && !isPending ? <AmazonCardsPanel /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/amazon-auto" element={isAuthenticated && !isPending ? <AmazonAutoPanel /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/amazon-create" element={isAuthenticated && !isPending ? <AmazonAccountCreator /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
            <Route path="/admin" element={isAuthenticated && (userRole === 'admin' || userRole === 'owner') ? <AdminPanel /> : <Navigate to="/" replace />} />
            <Route path="/virtual-operator" element={isAuthenticated && !isPending ? <VirtualOperator /> : <Navigate to={isPending ? "/profile" : "/login"} replace />} />
          </Routes>
        </div>

        {showBottomNav && <BottomNav unreadMessages={totalUnreadMessages} />}
        {isAuthenticated && showLiveStats && <LiveStats />}
        {showActivityFeed && <ActivityFeed />}
      </div>
    </ChatNotificationContext.Provider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppContent />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
